import { Box, Checkbox, FormControlLabel, Skeleton, Stack, Typography, useTheme } from "@mui/material";
import { useCallback, useMemo, useState, useEffect } from "react";
import { VirtualList } from "src/components";
import { ProductConfiguration } from "../configurations-list/components/product-configuration";
import { RootState } from "src/store";
import { connect } from "react-redux";
import { useConfiguratorPageT } from "src/translation-hooks/configurator-page";
import { useGenericTranslationsT } from "src/translation-hooks/generic-translations";
import { configuratorActions } from "src/pages/configurator/configurator-store";
import { AppliedFilters } from "../filter/components";
import { buildLabelIndex, getProductChipLabels, getProductDisplayName, canCopyItem } from "src/pages/configurator/configurator-utils";
import { NO_LABEL_FILTER_ID } from "../filter/filter-types";
import { Model_ConfigurationInvalidInformationGet, Model_ConfigurationMissingInformationGet, Model_ConfigurationsGet_SystemProductConfigurationItem } from "src/backend/openapi/v1";
import { TSortField } from "../../configurator-types";
import { groupProductsHierarchy, TFlatProductItem } from "./products-utils";
import { GroupNode, collectAllGroupNodePaths } from "../group-node";
import { NAME_COLUMN_MAX_FRACTION, PRODUCT_NAME_FALLBACK_WIDTH, PRODUCT_NAME_WIDTH_VARIABLE, PRODUCTS_TAB_ROW_OVERHEAD } from "../configurations-list/configurations-list-utils";
import { buildCanvasFont } from "src/utils/text-measure-utils";
import { useSharedColumnWidth } from "src/utils/hooks/useSharedColumnWidth";
import { CHIP_FONT_SIZE, CHIP_FONT_WEIGHT, estimateChipListWidth, estimateChipWidth } from "src/components/chip-list/chip-list-utils";

const mapStateToProps = (state: RootState) => ({
  configurationsState: state.pageReducer.combinedConfiguratorReducer.configuratorReducer.configurationsState,
  // Filtered copy of the dataset; undefined while no attribute filter is applied
  productsState: state.pageReducer.combinedConfiguratorReducer.configuratorReducer.productsState,
  isLoading: state.pageReducer.combinedConfiguratorReducer.configuratorReducer.isLoading,
  isProductsLoading: state.pageReducer.combinedConfiguratorReducer.configuratorReducer.isProductsLoading,
  appliedCustomFilters: state.pageReducer.combinedConfiguratorReducer.configuratorReducer.appliedCustomFilters,
  searchTerm: state.pageReducer.combinedConfiguratorReducer.configuratorReducer.searchTerm,
  labels: state.pageReducer.combinedConfiguratorReducer.configuratorReducer.labels,
  sortField: state.pageReducer.combinedConfiguratorReducer.configuratorReducer.sortField,
  sortDirection: state.pageReducer.combinedConfiguratorReducer.configuratorReducer.sortDirection,
  groupFields: state.pageReducer.combinedConfiguratorReducer.configuratorReducer.groupFields,
  groupSortBy: state.pageReducer.combinedConfiguratorReducer.configuratorReducer.groupSortBy,
  groupSortDirection: state.pageReducer.combinedConfiguratorReducer.configuratorReducer.groupSortDirection,
  expandCollapseSignal: state.pageReducer.combinedConfiguratorReducer.configuratorReducer.expandCollapseSignal,
  selectedProducts: state.pageReducer.combinedConfiguratorReducer.configuratorReducer.selectedProducts,
  viewSettings: state.pageReducer.combinedConfiguratorReducer.viewSettingsReducer.applied
});

const ownActions = {
  setSelectedProducts: configuratorActions.setSelectedProducts
};

const getSortValue = (item: Model_ConfigurationsGet_SystemProductConfigurationItem, field: TSortField, systemProductConfigurationName: string): string => {
  // "none" is never actually passed in (sortedItems below short-circuits before sorting), but the
  // field parameter's type now includes it, so this keeps the item[field] indexing below type-safe
  if (field === "none") return "";
  if (field === "configurationName") return systemProductConfigurationName ?? "";
  const val = item[field];
  if (val instanceof Date) return val.toISOString();
  return (val as string | null | undefined) ?? "";
};

type Props = typeof ownActions & ReturnType<typeof mapStateToProps>;

const ProductsBase = (props: Props) => {
  const {
    configurationsState,
    productsState,
    isLoading,
    isProductsLoading,
    appliedCustomFilters,
    searchTerm,
    labels,
    sortField,
    sortDirection,
    groupFields,
    groupSortBy,
    groupSortDirection,
    expandCollapseSignal,
    selectedProducts,
    viewSettings,
    setSelectedProducts
  } = props;

  const theme = useTheme();
  const configuratorPageT = useConfiguratorPageT();
  const genericTranslationsT = useGenericTranslationsT();

  // tracks which group-tree paths the user has explicitly opened; empty set = all collapsed by default
  const [expandedPaths, setExpandedPaths] = useState<Set<string>>(new Set());

  // reset to all-collapsed when the hierarchy changes — empty set needs no seeding, no post-render flash
  useEffect(() => {
    setExpandedPaths(new Set());
  }, [groupFields]);

  // build a Map<entityId, labels[]> once per labels change — avoids O(n) scans inside filteredData
  const labelIndex = useMemo(() => buildLabelIndex(labels), [labels]);

  // The server-filtered dataset when an attribute filter is applied, otherwise the shared unfiltered
  // one. Only this tab narrows its data — the Configurations tab always reads configurationsState.
  const productsData = productsState ?? configurationsState;

  const filteredData = useMemo(() => {
    if (!productsData) return [];

    // Missing information filter
    const wantMissingYes = appliedCustomFilters.some(x => x.filterType === "missingInfo" && x.id === "yes");
    const wantMissingNo = appliedCustomFilters.some(x => x.filterType === "missingInfo" && x.id === "no");

    const afterMissingInfoFilter = productsData.configurations.flatMap(configuration => {
      // systemProductConfigurationItemId is the renamed field on missing/invalid info models
      const missingIds = new Set((configuration.missingInformation || []).map(m => m.systemProductConfigurationItemId));

      // systemProductConfigurationItems replaces projectLineItems; items use systemProductConfigurationItemId
      const visibleItems = (configuration.systemProductConfigurationItems || []).filter(pli => {
        const hasMissing = missingIds.has(pli.systemProductConfigurationItemId || "");
        return (!wantMissingYes && !wantMissingNo) || (wantMissingYes && hasMissing) || (wantMissingNo && !hasMissing);
      });

      return visibleItems.length > 0 ? [{ configuration, visibleItems }] : [];
    });

    // Invalid information filter
    const wantInvalidYes = appliedCustomFilters.some(x => x.filterType === "invalidInfo" && x.id === "yes");
    const wantInvalidNo = appliedCustomFilters.some(x => x.filterType === "invalidInfo" && x.id === "no");

    const afterInvalidInfoFilter =
      !wantInvalidYes && !wantInvalidNo
        ? afterMissingInfoFilter
        : afterMissingInfoFilter.flatMap(({ configuration, visibleItems }) => {
            // systemProductConfigurationItemId is the renamed field on invalid info models
            const invalidIds = new Set((configuration.invalidInformation || []).map(m => m.systemProductConfigurationItemId));
            const matchingItems = visibleItems.filter(pli => {
              // include the item's own IsValid flag, same signal the row's warning icon checks
              const hasInvalid = invalidIds.has(pli.systemProductConfigurationItemId || "") || pli.isValid === false;
              return (wantInvalidYes && hasInvalid) || (wantInvalidNo && !hasInvalid);
            });
            return matchingItems.length > 0 ? [{ configuration, visibleItems: matchingItems }] : [];
          });

    // Product type filter
    const selectedProductTypeIds = new Set(appliedCustomFilters.filter(x => x.filterType === "productType").map(x => x.id));

    const afterProductTypeFilter =
      selectedProductTypeIds.size === 0
        ? afterInvalidInfoFilter
        : afterInvalidInfoFilter.flatMap(({ configuration, visibleItems }) => {
            const matchingItems = visibleItems.filter(pli => pli.productTypeId !== null && pli.productTypeId !== undefined && selectedProductTypeIds.has(pli.productTypeId));

            return matchingItems.length > 0 ? [{ configuration, visibleItems: matchingItems }] : [];
          });

    // Label filter — configuration-level match keeps all products; otherwise filter per product
    const selectedLabelIds = new Set(appliedCustomFilters.filter(x => x.filterType === "label").map(x => x.id));
    const afterLabelFilter =
      selectedLabelIds.size === 0
        ? afterProductTypeFilter
        : afterProductTypeFilter.flatMap(({ configuration, visibleItems }) => {
            // systemProductConfigurationId replaces configurationId on ConfigurationGroup
            const configLabels = labelIndex.get(configuration.systemProductConfigurationId ?? "") ?? [];
            if (configLabels.some(label => selectedLabelIds.has(label.labelId))) {
              return [{ configuration, visibleItems }];
            }
            const matchingItems = visibleItems.filter(pli => {
              // systemProductConfigurationItemId is the item ID on the list-view model
              const pliLabels = labelIndex.get(pli.systemProductConfigurationItemId ?? "") ?? [];
              // "no labels" checks only this product's own labels, independent of the parent configuration
              return pliLabels.some(label => selectedLabelIds.has(label.labelId)) || (selectedLabelIds.has(NO_LABEL_FILTER_ID) && pliLabels.length === 0);
            });
            return matchingItems.length > 0 ? [{ configuration, visibleItems: matchingItems }] : [];
          });

    // Search
    const term = searchTerm.trim().toLowerCase();
    if (!term) return afterLabelFilter;

    // Products tab shows individual product cards, so search narrows down to only the
    // matching products — unlike the Configurations tab, which reveals the whole configuration
    return afterLabelFilter.flatMap(({ configuration, visibleItems }) => {
      const configNameMatches = configuration.systemProductConfigurationName?.toLowerCase().includes(term);
      // configuration-level labels — a match here keeps all products intact, same as a name match
      const configLabels = labelIndex.get(configuration.systemProductConfigurationId ?? "") ?? [];
      const configLabelMatches = configLabels.some(label => label.shortname.toLowerCase().includes(term));

      const matchingItems = visibleItems.filter(pli => {
        if (configNameMatches || configLabelMatches) return true;
        if (pli.productDescription?.toLowerCase().includes(term)) return true;
        if (pli.productNumber?.toLowerCase().includes(term)) return true;
        if (pli.productTypeName?.toLowerCase().includes(term)) return true;

        // O(1) lookup via pre-built index; systemProductConfigurationItemId is the item key in list view
        const pliLabels = labelIndex.get(pli.systemProductConfigurationItemId ?? "") ?? [];
        return pliLabels.some(label => label.shortname.toLowerCase().includes(term));
      });

      return matchingItems.length > 0 ? [{ configuration, visibleItems: matchingItems }] : [];
    });
  }, [productsData, appliedCustomFilters, searchTerm, labelIndex]);

  // sort only — no labels dependency, only reruns when data or sort settings change
  const sortedItems = useMemo(() => {
    const flattened = filteredData.flatMap(({ configuration, visibleItems }) => visibleItems.map(projectLineItem => ({ configuration, projectLineItem })));
    // "none" means no client-side sort is applied — keep the API's own returned order
    if (sortField === "none") return flattened;
    return flattened.sort((a, b) => {
      const aVal = getSortValue(a.projectLineItem, sortField, a.configuration.systemProductConfigurationName ?? "");
      const bVal = getSortValue(b.projectLineItem, sortField, b.configuration.systemProductConfigurationName ?? "");
      const cmp = aVal.localeCompare(bVal, undefined, { sensitivity: "base" });
      return sortDirection === "asc" ? cmp : -cmp;
    });
  }, [filteredData, sortField, sortDirection]);

  // only computed when grouped view is active — flat list uses sortedItems directly
  const groups = useMemo(() => {
    if (groupFields.length === 0) return [];
    return groupProductsHierarchy(sortedItems, groupFields, labelIndex, configuratorPageT, groupSortBy, groupSortDirection);
  }, [sortedItems, groupFields, groupSortBy, groupSortDirection, labelIndex, configuratorPageT]);

  // Collapse All / Expand All, triggered from the header — requestId (not just the action) is the
  // dep so a repeated identical click still re-fires this even though the Set contents don't change
  useEffect(() => {
    if (!expandCollapseSignal) return;
    setExpandedPaths(expandCollapseSignal.action === "expand" ? new Set(collectAllGroupNodePaths(groups)) : new Set());
  }, [expandCollapseSignal?.requestId]);

  // memoized so it only recomputes when filteredData changes, not on sort/group changes
  const totalProjectLineItems = useMemo(() => filteredData.reduce((acc, { visibleItems }) => acc + visibleItems.length, 0), [filteredData]);

  // sortedItems covers both views — grouping buckets the same items
  const visibleProductIds = useMemo(() => sortedItems.map(({ projectLineItem }) => projectLineItem.systemProductConfigurationItemId ?? "").filter(id => id !== ""), [sortedItems]);

  // Rows receive their own isSelected boolean rather than subscribing to the selection array, so a
  // toggle re-renders one row instead of all of them. The Set keeps that lookup O(1) per row.
  const selectedProductIds = useMemo(() => new Set(selectedProducts), [selectedProducts]);

  const isAllSelected = visibleProductIds.length > 0 && visibleProductIds.every(id => selectedProductIds.has(id));

  // One name-column width for the whole tab, matching the configurations list — see useSharedColumnWidth
  const [listContainer, setListContainer] = useState<HTMLDivElement | null>(null);

  const productNames = useMemo(() => sortedItems.map(({ projectLineItem }) => getProductDisplayName(projectLineItem)), [sortedItems]);

  // 500 is the weight the product name cell overrides the theme's 600 with
  const productNameFont = useMemo(() => buildCanvasFont(500, theme.typography.subtitle2.fontSize, theme.typography.fontFamily), [theme]);
  const chipFont = useMemo(() => buildCanvasFont(CHIP_FONT_WEIGHT, CHIP_FONT_SIZE, theme.typography.fontFamily), [theme]);

  // Labels come first — the name column only gets what the most-loaded chip row leaves behind. This
  // tab also renders a configuration-name chip in the actions group, which has to be reserved too.
  const widestChipRow = useMemo(
    () =>
      sortedItems.reduce((widest, { configuration, projectLineItem }) => {
        const chips = estimateChipListWidth(getProductChipLabels(projectLineItem, labelIndex, viewSettings.selectedItemIds), chipFont);
        const configurationChip = estimateChipWidth(configuration.systemProductConfigurationName ?? "", chipFont);
        return Math.max(widest, chips + configurationChip);
      }, 0),
    [sortedItems, labelIndex, viewSettings.selectedItemIds, chipFont]
  );

  const productNameWidth = useSharedColumnWidth(listContainer, productNames, productNameFont, {
    maxFraction: NAME_COLUMN_MAX_FRACTION,
    minWidth: PRODUCT_NAME_FALLBACK_WIDTH,
    extraWidth: 2,
    reservedWidth: PRODUCTS_TAB_ROW_OVERHEAD + widestChipRow
  });

  const nameColumnSx = useMemo(() => ({ [PRODUCT_NAME_WIDTH_VARIABLE]: `${productNameWidth ?? PRODUCT_NAME_FALLBACK_WIDTH}px` }), [productNameWidth]);

  const renderProduct = useCallback(
    // Derived from sortedItems rather than TFlatProductItem: the latter is the deliberately loose
    // shape the grouping helpers work against, and narrows missing/invalid info away
    ({ configuration, projectLineItem }: (typeof sortedItems)[number]) => (
      <ProductConfiguration
        missingInformation={configuration.missingInformation || []}
        invalidInformation={configuration.invalidInformation || []}
        projectLineItem={projectLineItem}
        systemProductConfigurationName={configuration.systemProductConfigurationName ?? undefined}
        configurationId={configuration.systemProductConfigurationId || ""}
        labelIndex={labelIndex}
        isProductTab
        isSelectable
        isSelected={selectedProductIds.has(projectLineItem.systemProductConfigurationItemId ?? "")}
        canCopy={canCopyItem(projectLineItem)}
      />
    ),
    [labelIndex, selectedProductIds]
  );

  if (isLoading || isProductsLoading) {
    return (
      <Stack gap={2}>
        {Array(4)
          .fill(null)
          .map((_, index) => (
            <Skeleton key={index} height={40} />
          ))}
      </Stack>
    );
  }

  if (!isLoading && productsData) {
    return (
      <Stack ref={setListContainer} gap={1} flex={1} minHeight={0} overflow="hidden" sx={nameColumnSx}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" mx={1} flexShrink={0}>
          <Stack direction="row" alignItems="center" gap={1}>
            {totalProjectLineItems > 0 && (
              <FormControlLabel
                sx={{
                  "& .MuiFormControlLabel-label": {
                    fontSize: theme.typography.subtitle1.fontSize,
                    fontWeight: "normal",
                    fontFamily: "AvenirNextLTPro, sans-serif"
                  }
                }}
                checked={isAllSelected}
                onChange={(_, checked) => setSelectedProducts(checked ? visibleProductIds : [])}
                control={<Checkbox title={genericTranslationsT.selectAll} size="small" />}
                label={genericTranslationsT.selectAll}
              />
            )}
            <AppliedFilters />
          </Stack>

          {totalProjectLineItems > 0 && (
            <Typography flex="none" variant="subtitle1" color={theme.palette.customColors.darkGrey}>
              {totalProjectLineItems} {totalProjectLineItems > 1 ? configuratorPageT.products : configuratorPageT.product}
            </Typography>
          )}
        </Stack>

        {filteredData.length === 0 && (
          <Stack gap={2} mt={2}>
            <Typography variant="subtitle2" color={theme.palette.customColors.darkGrey}>
              {configuratorPageT.noConfigurations}
            </Typography>
            <Typography variant="subtitle1" color={theme.palette.customColors.darkGrey}>
              {configuratorPageT.getStartedByClickingNewConfiguration}
            </Typography>
          </Stack>
        )}

        {filteredData.length > 0 && (
          <>
            {/* Flat list — windowed, and mounted only when no grouping is active. It used to stay
                mounted so toggling group-by back to "none" was a CSS change rather than a mount, but
                that meant every line item in the project was live in the DOM the whole time. With
                only the visible rows mounted, the remount is cheap. */}
            {groupFields.length === 0 && (
              <VirtualList items={sortedItems} getItemKey={({ projectLineItem }) => projectLineItem.systemProductConfigurationItemId ?? ""} renderItem={renderProduct} estimateSize={44} gap={8} />
            )}

            {/* Grouped view — only mounted when at least one group field is active */}
            {groupFields.length > 0 && (
              <Box sx={{ flex: 1, minHeight: 0, overflow: "auto" }}>
                <Stack gap={1}>
                  {groups.map(group => (
                    <GroupNode<TFlatProductItem>
                      key={group.key}
                      node={group}
                      parentPath=""
                      expandedPaths={expandedPaths}
                      onToggle={(path, expanded) =>
                        setExpandedPaths(prev => {
                          const next = new Set(prev);
                          if (expanded) next.add(path);
                          else next.delete(path);
                          return next;
                        })
                      }
                      itemCountLabel={count => `${count} ${count !== 1 ? configuratorPageT.products : configuratorPageT.product}`}
                      // no isProductTab — cards use lightGrey2 background, matching configuration.tsx accordion
                      renderItems={items => (
                        <Stack gap={1.4}>
                          {items.map(({ configuration, projectLineItem }) => (
                            <ProductConfiguration
                              key={projectLineItem.systemProductConfigurationItemId}
                              missingInformation={configuration.missingInformation as Model_ConfigurationMissingInformationGet[]}
                              invalidInformation={configuration.invalidInformation as Model_ConfigurationInvalidInformationGet[]}
                              projectLineItem={projectLineItem}
                              systemProductConfigurationName={configuration.systemProductConfigurationName ?? undefined}
                              configurationId={configuration.systemProductConfigurationId || ""}
                              labelIndex={labelIndex}
                              isSelectable
                              isSelected={selectedProductIds.has(projectLineItem.systemProductConfigurationItemId ?? "")}
                              canCopy={canCopyItem(projectLineItem)}
                            />
                          ))}
                        </Stack>
                      )}
                    />
                  ))}
                </Stack>
              </Box>
            )}
          </>
        )}
      </Stack>
    );
  }

  return <></>;
};

export const Products = connect(mapStateToProps, ownActions)(ProductsBase);
